import React from 'react';
import moment from 'moment';
import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import {
  getRenterListingQualifications,
  LISTING_QUALIFICATION_BUDGET_DISQUALIFIED,
  LISTING_QUALIFICATION_MOVE_PERIOD_BEFORE_FUTURE_LISTING_AVAILABLE_DATE_RANGE,
  LISTING_QUALIFICATION_MOVE_PERIOD_AFTER_FUTURE_LISTING_AVAILABLE_DATE_RANGE,
  LISTING_QUALIFICATION_MOVE_PERIOD_DOES_NOT_FIT_PAST_LISTING_AVAILABLE_DATE_RANGE,
  LISTING_QUALIFICATION_PETS_NONE_ALLOWED,
  LISTING_QUALIFICATION_PETS_TOO_MANY,
} from 'common/householdApplication/prequalificationHelpers';
import {
  numberWithCommas,
} from 'common/formatters';
import { getListingAvailableDateRange } from 'common/listingHelpers';
import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_OCCUPANT_TYPE_TENANT,
  LISTING_OCCUPANT_TYPE_OWNER,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

export function getOccupancyCellContentFromListing(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  if (listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT) {
    return LISTING_OCCUPANT_TYPE_VACANT;
  }
  switch (listing.occupantType) {
    case LISTING_OCCUPANT_TYPE_VACANT:
      return LISTING_OCCUPANT_TYPE_VACANT;
    case LISTING_OCCUPANT_TYPE_OWNER:
      return [
        <span key='tooltip' className='tooltip'>Owner</span>,
        `${listing.occupantPhone ? listing.occupantPhone : ''}`,
      ];
    case LISTING_OCCUPANT_TYPE_TENANT:
      return [
        <span key='tooltip' className='tooltip'>Tenant</span>,
        `${listing.occupantPhone ? listing.occupantPhone : 'Tenant (No phone)'}`,
      ];
    default:
      return null;
  }
}

export function isTourUserPrequalCompleted(tour) {
  typeCheck('tour::NonEmptyObject', tour);

  if (!tour.renterProfile) {
    return false;
  }

  return tour.renterProfile.isPrequalCompleted;
}

export function getShowingDisqualifications({ tour, listing }) {
  typeCheck('tour::NonEmptyObject', tour);
  typeCheck('listing::NonEmptyObject', listing);
  const results = {
    isDisqualified: false,
    disqualifications: [],
  };
  if (!isTourUserPrequalCompleted(tour)) {
    return results;
  }

  const renterListingQualifications = getRenterListingQualifications(tour.renterProfile, listing);
  const formattedMoveInPeriod = (
    `${momentWithAustinTimezone(tour.renterProfile.movePeriodStartDate).format('MMMM Do')} - ` +
    `${momentWithAustinTimezone(tour.renterProfile.movePeriodEndDate).format('MMMM Do')}`
  );
  const listingAvailableDateRange = getListingAvailableDateRange({ listing });
  const formattedListingAvailableDateRangeStartDateMoment = moment(listingAvailableDateRange.startDate).utc().format('MMMM Do'); // eslint-disable-line max-len
  const formattedListingAvailableDateRangeEndDateMoment = moment(listingAvailableDateRange.endDate).utc().format('MMMM Do'); // eslint-disable-line max-len

  if (renterListingQualifications.availableDate === LISTING_QUALIFICATION_MOVE_PERIOD_BEFORE_FUTURE_LISTING_AVAILABLE_DATE_RANGE) { // eslint-disable-line max-len
    results.isDisqualified = true;
    results.disqualifications.push(
      `This home is not available until ${formattedListingAvailableDateRangeStartDateMoment}, ` +
      `after the renter's move-in period (${formattedMoveInPeriod}).`
    );
  }
  else if (renterListingQualifications.availableDate === LISTING_QUALIFICATION_MOVE_PERIOD_AFTER_FUTURE_LISTING_AVAILABLE_DATE_RANGE) { // eslint-disable-line max-len
    results.isDisqualified = true;
    results.disqualifications.push(
      `The home is available on ${formattedListingAvailableDateRangeStartDateMoment}. ` +
      `The renter must pay rent by ${formattedListingAvailableDateRangeEndDateMoment}, ` + // eslint-disable-line max-len
      `before their move period (${formattedMoveInPeriod}).`
    );
  }
  else if (renterListingQualifications.availableDate === LISTING_QUALIFICATION_MOVE_PERIOD_DOES_NOT_FIT_PAST_LISTING_AVAILABLE_DATE_RANGE) { // eslint-disable-line max-len
    results.isDisqualified = true;
    results.disqualifications.push(
      'This home is available now so the renter must start paying rent within 2 weeks ' +
      `(${formattedListingAvailableDateRangeEndDateMoment} at the latest).`
    );
  }

  if (renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_DISQUALIFIED) {
    results.isDisqualified = true;
    const listingRequiredMonthlyIncome = listing.price * 3;
    const listingRequiredYearlyIncome = listingRequiredMonthlyIncome * 12;
    const householdYearlyIncomeDisplay = numberWithCommas(
      Math.round(tour.renterProfile.prequalHouseholdYearlyIncome)
    );
    results.disqualifications.push(
      `Household yearly income is $${householdYearlyIncomeDisplay}. ` +
      `Home rent is $${numberWithCommas(listing.price)}. ` +
      `They need to make at least $${numberWithCommas(listingRequiredYearlyIncome)}.`
    );
  }

  if (tour.renterProfile.petCount) {
    if (renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_NONE_ALLOWED) {
      results.isDisqualified = true;
      results.disqualifications.push('No pets allowed!');
    }
    else if (renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_TOO_MANY) {
      results.isDisqualified = true;
      const nonEsaPets = tour.renterProfile.pets.filter((pet) => {
        return !pet.isEsa;
      });
      results.disqualifications.push(
        `Renter has ${nonEsaPets.length} pets. ` +
        `Max pets allowed is ${listing.maximumPetsAllowed}.`
      );
    }
  }

  return results;
}
